import React, { useState, useEffect, useCallback } from 'react';
import { useAdmin } from '../../components/Admin/AdminContext';
import { 
  Activity, Server, Database, HardDrive, Cpu, MemoryStick,
  Wifi, WifiOff, RefreshCw, Clock, AlertTriangle, CheckCircle,
  XCircle, Gauge, Network, Zap, Timer, TrendingUp, Box, 
  Thermometer, Cloud, Globe, BarChart, Layers, FileText, ArrowRight
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatUptime = (seconds) => {
  if (!seconds) return 'N/A';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  
  const parts = [];
  if (days > 0) parts.push(`${days}j`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  return parts.join(' ') || '< 1m';
};

const getHealthColor = (status) => {
  if (status === 'healthy' || status === 'established') return 'text-green-600';
  if (status === 'warning' || status === 'degraded') return 'text-yellow-600';
  return 'text-red-600';
};

const getHealthBg = (status) => {
  if (status === 'healthy' || status === 'established') return 'bg-green-50 border-green-200';
  if (status === 'warning' || status === 'degraded') return 'bg-yellow-50 border-yellow-200';
  return 'bg-red-50 border-red-200';
};

export default function AdminStatus() {
  const { adminToken } = useAdmin();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  
  // Métriques
  const [aiServerMetrics, setAiServerMetrics] = useState(null);
  const [aiPerformance, setAiPerformance] = useState(null);
  const [cdnMetrics, setCdnMetrics] = useState(null);
  const [platformMetrics, setPlatformMetrics] = useState(null);
  
  // Modale des logs
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [aiLogs, setAiLogs] = useState([]);
  const [selectedLog, setSelectedLog] = useState(null);

  const fetchAllMetrics = useCallback(async () => {
    try {
      const headers = { 'Authorization': `Bearer ${adminToken}` };
      
      const [aiServer, aiPerf, cdn, platform] = await Promise.all([
        fetch(`${API_URL}/api/admin/monitoring/ai-server`, { headers }).then(r => r.json()),
        fetch(`${API_URL}/api/admin/monitoring/ai-performance`, { headers }).then(r => r.json()),
        fetch(`${API_URL}/api/admin/monitoring/cdn`, { headers }).then(r => r.json()),
        fetch(`${API_URL}/api/admin/monitoring/platform`, { headers }).then(r => r.json())
      ]);
      
      setAiServerMetrics(aiServer);
      setAiPerformance(aiPerf);
      setCdnMetrics(cdn);
      setPlatformMetrics(platform);
      setLastUpdate(new Date());
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [adminToken]);

  const fetchAILogs = useCallback(async () => {
    try {
      const headers = { 'Authorization': `Bearer ${adminToken}` };
      const response = await fetch(`${API_URL}/api/admin/monitoring/ai-logs?limit=100`, { headers });
      const data = await response.json();
      setAiLogs(data.logs || []);
    } catch (err) {
      console.error('Error fetching AI logs:', err);
    }
  }, [adminToken]);

  const openLogsModal = () => {
    setShowLogsModal(true);
    fetchAILogs();
  };

  useEffect(() => {
    fetchAllMetrics();
  }, [fetchAllMetrics]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchAllMetrics, 10000); // Refresh toutes les 10s
    return () => clearInterval(interval);
  }, [autoRefresh, fetchAllMetrics]);

  if (loading && !aiServerMetrics) {
    return (
      <div className="p-6 flex items-center justify-center h-96">
        <div className="text-center">
          <RefreshCw className="h-12 w-12 animate-spin mx-auto text-blue-600" />
          <p className="mt-4 text-gray-500">Chargement des métriques...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Activity className="h-7 w-7 text-blue-600" />
            Monitoring Système
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Surveillance en temps réel de l'infrastructure Sun-Spot
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="text-sm text-gray-500">
            {lastUpdate && `Dernière mise à jour: ${lastUpdate.toLocaleTimeString('fr-FR')}`}
          </div>
          <Button
            onClick={() => {
              setAutoRefresh(!autoRefresh);
            }}
            variant={autoRefresh ? 'default' : 'outline'}
            size="sm"
          >
            {autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
          </Button>
          <Button onClick={fetchAllMetrics} size="sm" variant="outline">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            {error}
          </p>
        </div>
      )}

      {/* Grid principal */}
      <div className="grid gap-6 md:grid-cols-2">
        
        {/* SERVEUR IA */}
        <Card className={getHealthBg(aiServerMetrics?.status)}>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Server className="h-5 w-5" />
                Serveur IA
              </div>
              <Badge className={aiServerMetrics?.status === 'healthy' ? 'bg-green-500' : 'bg-red-500'}>
                {aiServerMetrics?.connection === 'established' ? 'Connecté' : 'Déconnecté'}
              </Badge>
            </CardTitle>
            <CardDescription>
              83.204.119.173:8001 • Latence: {aiServerMetrics?.latency_ms?.toFixed(0) || '?'}ms
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {aiServerMetrics?.status === 'healthy' ? (
              <>
                {/* GPU */}
                {aiServerMetrics.gpu?.available && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2 text-gray-700">
                        <Zap className="h-4 w-4 text-purple-600" />
                        GPU (NVIDIA)
                      </span>
                      <span className="font-medium">{aiServerMetrics.gpu.utilization}%</span>
                    </div>
                    <Progress value={aiServerMetrics.gpu.utilization} className="h-2" />
                    <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                      <div>VRAM: {aiServerMetrics.gpu.memory_used_mb}MB / {aiServerMetrics.gpu.memory_total_mb}MB</div>
                      <div className="flex items-center gap-1">
                        <Thermometer className="h-3 w-3" />
                        {aiServerMetrics.gpu.temperature_c}°C
                      </div>
                    </div>
                  </div>
                )}
                
                {/* CPU */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 text-gray-700">
                      <Cpu className="h-4 w-4 text-blue-600" />
                      CPU ({aiServerMetrics.cpu?.count} cœurs)
                    </span>
                    <span className="font-medium">{aiServerMetrics.cpu?.percent}%</span>
                  </div>
                  <Progress value={aiServerMetrics.cpu?.percent} className="h-2" />
                </div>
                
                {/* RAM */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 text-gray-700">
                      <MemoryStick className="h-4 w-4 text-green-600" />
                      RAM
                    </span>
                    <span className="font-medium">{aiServerMetrics.memory?.percent}%</span>
                  </div>
                  <Progress value={aiServerMetrics.memory?.percent} className="h-2" />
                  <div className="text-xs text-gray-600">
                    {aiServerMetrics.memory?.used_gb}GB / {aiServerMetrics.memory?.total_gb}GB
                  </div>
                </div>
                
                {/* Disque */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 text-gray-700">
                      <HardDrive className="h-4 w-4 text-orange-600" />
                      Disque
                    </span>
                    <span className="font-medium">{aiServerMetrics.disk?.percent}%</span>
                  </div>
                  <Progress value={aiServerMetrics.disk?.percent} className="h-2" />
                  <div className="text-xs text-gray-600">
                    {aiServerMetrics.disk?.used_gb}GB / {aiServerMetrics.disk?.total_gb}GB
                  </div>
                </div>
                
                {/* Uptime */}
                <div className="flex items-center justify-between text-sm pt-2 border-t">
                  <span className="text-gray-600">Uptime</span>
                  <span className="font-medium">{formatUptime(aiServerMetrics.uptime_seconds)}</span>
                </div>
              </>
            ) : (
              <div className="text-center py-4">
                <WifiOff className="h-8 w-8 text-red-500 mx-auto mb-2" />
                <p className="text-red-600 text-sm">{aiServerMetrics?.error || 'Serveur IA indisponible'}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* PERFORMANCES IA */}
        <Card className="bg-purple-50 border-purple-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-purple-600" />
              Performances IA
            </CardTitle>
            <CardDescription>
              Statistiques de traitement
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {aiPerformance?.status === 'healthy' || aiPerformance?.status === 'no_data' ? (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg p-3 border">
                    <p className="text-xs text-gray-500 mb-1">Total scans</p>
                    <p className="text-2xl font-bold text-gray-900">{aiPerformance.total_scans}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border">
                    <p className="text-xs text-gray-500 mb-1">Temps moyen</p>
                    <p className="text-2xl font-bold text-gray-900">{aiPerformance.avg_response_time_s}s</p>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Min</span>
                    <span className="font-medium text-green-600">{aiPerformance.min_response_time_s}s</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Max</span>
                    <span className="font-medium text-orange-600">{aiPerformance.max_response_time_s}s</span>
                  </div>
                </div>
              </>
            ) : (
              <p className="text-red-600 text-sm">Erreur de chargement</p>
            )}
          </CardContent>
        </Card>

        {/* CDN CLOUDFLARE */}
        <Card className="bg-blue-50 border-blue-200">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Cloud className="h-5 w-5 text-blue-600" />
                CDN Cloudflare R2
              </div>
              <Badge className={cdnMetrics?.available ? 'bg-green-500' : 'bg-red-500'}>
                {cdnMetrics?.available ? 'Opérationnel' : 'Dégradé'}
              </Badge>
            </CardTitle>
            <CardDescription>
              cdn.sun-spot.app • Latence: {cdnMetrics?.latency_ms?.toFixed(0) || '?'}ms
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {cdnMetrics?.status !== 'error' ? (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg p-3 border">
                    <p className="text-xs text-gray-500 mb-1">Objets stockés</p>
                    <p className="text-2xl font-bold text-gray-900">{cdnMetrics?.total_objects || 0}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border">
                    <p className="text-xs text-gray-500 mb-1">Stockage</p>
                    <p className="text-2xl font-bold text-gray-900">{cdnMetrics?.estimated_size_mb?.toFixed(0) || 0} MB</p>
                  </div>
                </div>
                
                <div className="bg-white rounded-lg p-3 border">
                  <p className="text-xs text-gray-500 mb-1">Bucket</p>
                  <p className="text-sm font-mono text-gray-700">{cdnMetrics?.bucket || 'sun-spot-cdn'}</p>
                </div>
              </>
            ) : (
              <div className="text-center py-4">
                <WifiOff className="h-8 w-8 text-red-500 mx-auto mb-2" />
                <p className="text-red-600 text-sm">{cdnMetrics?.error || 'CDN indisponible'}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* PLATEFORME */}
        <Card className="bg-green-50 border-green-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-green-600" />
              Serveur Plateforme
            </CardTitle>
            <CardDescription>
              platform.sun-spot.app
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {platformMetrics?.status === 'healthy' ? (
              <>
                {/* CPU */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 text-gray-700">
                      <Cpu className="h-4 w-4" />
                      CPU
                    </span>
                    <span className="font-medium">{platformMetrics.cpu_percent}%</span>
                  </div>
                  <Progress value={platformMetrics.cpu_percent} className="h-2" />
                </div>
                
                {/* RAM */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 text-gray-700">
                      <MemoryStick className="h-4 w-4" />
                      RAM
                    </span>
                    <span className="font-medium">{platformMetrics.memory_percent}%</span>
                  </div>
                  <Progress value={platformMetrics.memory_percent} className="h-2" />
                  <div className="text-xs text-gray-600">
                    {platformMetrics.memory_used_gb}GB / {platformMetrics.memory_total_gb}GB
                  </div>
                </div>
                
                {/* Disque */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 text-gray-700">
                      <HardDrive className="h-4 w-4" />
                      Disque
                    </span>
                    <span className="font-medium">{platformMetrics.disk_percent}%</span>
                  </div>
                  <Progress value={platformMetrics.disk_percent} className="h-2" />
                  <div className="text-xs text-gray-600">
                    {platformMetrics.disk_used_gb}GB / {platformMetrics.disk_total_gb}GB
                  </div>
                </div>
                
                {/* Database */}
                <div className="bg-white rounded-lg p-3 border">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 flex items-center gap-2">
                      <Database className="h-4 w-4" />
                      Base de données
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                    <div>
                      <p className="text-gray-500">Utilisateurs</p>
                      <p className="font-bold text-gray-900">{platformMetrics.database?.total_users || 0}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Spots</p>
                      <p className="font-bold text-gray-900">{platformMetrics.database?.total_spots || 0}</p>
                    </div>
                  </div>
                </div>
                
                {/* Uptime */}
                <div className="flex items-center justify-between text-sm pt-2 border-t">
                  <span className="text-gray-600">Uptime</span>
                  <span className="font-medium">{formatUptime(platformMetrics.uptime_seconds)}</span>
                </div>
              </>
            ) : (
              <p className="text-red-600 text-sm">Erreur de chargement</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Section détails IA - Pleine largeur */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart className="h-5 w-5 text-purple-600" />
            Analyse Détaillée du Serveur IA
          </CardTitle>
        </CardHeader>
        <CardContent>
          {aiServerMetrics?.status === 'healthy' && (
            <div className="grid md:grid-cols-4 gap-4">
              {/* CPU Details */}
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <div className="flex items-center justify-between mb-2">
                  <Cpu className="h-5 w-5 text-blue-600" />
                  <span className="text-2xl font-bold text-gray-900">{aiServerMetrics.cpu?.percent}%</span>
                </div>
                <p className="text-sm text-gray-600">CPU Global</p>
                <p className="text-xs text-gray-500 mt-1">{aiServerMetrics.cpu?.count} cœurs disponibles</p>
              </div>
              
              {/* RAM Details */}
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <div className="flex items-center justify-between mb-2">
                  <MemoryStick className="h-5 w-5 text-green-600" />
                  <span className="text-2xl font-bold text-gray-900">{aiServerMetrics.memory?.percent}%</span>
                </div>
                <p className="text-sm text-gray-600">Mémoire RAM</p>
                <p className="text-xs text-gray-500 mt-1">
                  {aiServerMetrics.memory?.available_gb}GB libre sur {aiServerMetrics.memory?.total_gb}GB
                </p>
              </div>
              
              {/* Disk Details */}
              <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                <div className="flex items-center justify-between mb-2">
                  <HardDrive className="h-5 w-5 text-orange-600" />
                  <span className="text-2xl font-bold text-gray-900">{aiServerMetrics.disk?.percent}%</span>
                </div>
                <p className="text-sm text-gray-600">Espace Disque</p>
                <p className="text-xs text-gray-500 mt-1">
                  {aiServerMetrics.disk?.free_gb}GB libre sur {aiServerMetrics.disk?.total_gb}GB
                </p>
              </div>
              
              {/* Network Details */}
              <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                <div className="flex items-center justify-between mb-2">
                  <Network className="h-5 w-5 text-purple-600" />
                  <span className="text-lg font-bold text-gray-900">{aiServerMetrics.network?.bytes_recv_mb?.toFixed(0)}MB</span>
                </div>
                <p className="text-sm text-gray-600">Données Reçues</p>
                <p className="text-xs text-gray-500 mt-1">
                  Envoyées: {aiServerMetrics.network?.bytes_sent_mb?.toFixed(0)}MB
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Résumé rapide */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-5 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90">Connexion IA</p>
              <p className="text-3xl font-bold mt-1">{aiServerMetrics?.latency_ms?.toFixed(0) || '?'}</p>
              <p className="text-xs opacity-75 mt-1">millisecondes</p>
            </div>
            <Timer className="h-12 w-12 opacity-50" />
          </div>
        </div>
        
        <div 
          className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-5 text-white cursor-pointer hover:from-purple-600 hover:to-purple-700 transition-all"
          onClick={openLogsModal}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90">Scans IA Totaux</p>
              <p className="text-3xl font-bold mt-1">{aiPerformance?.total_scans || 0}</p>
              <p className="text-xs opacity-75 mt-1">analyses complétées • Cliquez pour voir les logs</p>
            </div>
            <BarChart className="h-12 w-12 opacity-50" />
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-5 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90">Objets CDN</p>
              <p className="text-3xl font-bold mt-1">{cdnMetrics?.total_objects || 0}</p>
              <p className="text-xs opacity-75 mt-1">{cdnMetrics?.estimated_size_mb?.toFixed(0) || 0} MB stockés</p>
            </div>
            <Layers className="h-12 w-12 opacity-50" />
          </div>
        </div>
      </div>

      {/* Modale des Logs IA */}
      <Dialog open={showLogsModal} onOpenChange={setShowLogsModal}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-purple-600" />
              Logs des Scans IA
            </DialogTitle>
            <DialogDescription>
              Historique complet des analyses effectuées par l'intelligence artificielle
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 mt-4">
            {aiLogs.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>Aucun log disponible</p>
              </div>
            ) : (
              aiLogs.map((log, index) => (
                <div 
                  key={index}
                  className="border rounded-lg overflow-hidden hover:shadow-md transition-shadow"
                >
                  {/* Log Header */}
                  <div 
                    className="bg-gray-50 p-4 cursor-pointer hover:bg-gray-100"
                    onClick={() => setSelectedLog(selectedLog === index ? null : index)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${
                          log.scan_type === 'content' ? 'bg-purple-500' : 'bg-blue-500'
                        }`} />
                        <div>
                          <p className="font-medium text-gray-900">
                            {log.title || `Spot ${log.spot_id?.slice(0, 8)}`}
                          </p>
                          <p className="text-xs text-gray-500">
                            {log.timestamp ? new Date(log.timestamp).toLocaleString('fr-FR') : 'Date inconnue'}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        {log.processing_time && (
                          <Badge variant="outline" className="bg-purple-50">
                            <Timer className="h-3 w-3 mr-1" />
                            {log.processing_time.toFixed(2)}s
                          </Badge>
                        )}
                        <Badge className={log.scan_type === 'content' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}>
                          {log.scan_type === 'content' ? 'Optimisation Contenu' : 'Validation Géo'}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  
                  {/* Log Details (Expandable) */}
                  {selectedLog === index && (
                    <div className="p-4 bg-white border-t space-y-4">
                      {log.scan_type === 'content' ? (
                        <>
                          {/* Titre */}
                          {log.ai_results?.optimized_title && (
                            <div className="grid md:grid-cols-2 gap-4">
                              <div>
                                <p className="text-xs text-gray-500 mb-1">📝 Titre Original</p>
                                <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded">{log.original_data?.title}</p>
                              </div>
                              <div>
                                <p className="text-xs text-purple-600 mb-1 flex items-center gap-1">
                                  <ArrowRight className="h-3 w-3" />
                                  Titre Optimisé par IA
                                </p>
                                <p className="text-sm text-gray-900 bg-purple-50 p-3 rounded font-medium">{log.ai_results.optimized_title}</p>
                              </div>
                            </div>
                          )}
                          
                          {/* Description */}
                          {log.ai_results?.optimized_description && (
                            <div className="grid md:grid-cols-2 gap-4">
                              <div>
                                <p className="text-xs text-gray-500 mb-1">📄 Description Originale</p>
                                <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded max-h-32 overflow-y-auto">
                                  {log.original_data?.description}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs text-purple-600 mb-1 flex items-center gap-1">
                                  <ArrowRight className="h-3 w-3" />
                                  Description Optimisée par IA
                                </p>
                                <p className="text-sm text-gray-900 bg-purple-50 p-3 rounded font-medium max-h-32 overflow-y-auto">
                                  {log.ai_results.optimized_description}
                                </p>
                              </div>
                            </div>
                          )}
                          
                          {/* Tags */}
                          {log.ai_results?.suggested_tags?.length > 0 && (
                            <div className="grid md:grid-cols-2 gap-4">
                              <div>
                                <p className="text-xs text-gray-500 mb-1">🏷️ Tags Originaux</p>
                                <div className="flex flex-wrap gap-1">
                                  {(log.original_data?.tags || []).map((tag, i) => (
                                    <span key={i} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                                      {tag}
                                    </span>
                                  ))}
                                </div>
                              </div>
                              <div>
                                <p className="text-xs text-purple-600 mb-1 flex items-center gap-1">
                                  <ArrowRight className="h-3 w-3" />
                                  Tags Suggérés par IA
                                </p>
                                <div className="flex flex-wrap gap-1">
                                  {log.ai_results.suggested_tags.map((tag, i) => (
                                    <span key={i} className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded font-medium">
                                      {tag}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        /* Validation Géographique */
                        log.ai_results?.geo_validation && (
                          <div className="space-y-3">
                            <div className="grid md:grid-cols-3 gap-4">
                              <div className="bg-blue-50 p-3 rounded-lg">
                                <p className="text-xs text-blue-600 mb-1">Verdict</p>
                                <p className="text-lg font-bold text-gray-900">{log.ai_results.geo_validation.verdict}</p>
                              </div>
                              <div className="bg-green-50 p-3 rounded-lg">
                                <p className="text-xs text-green-600 mb-1">Score</p>
                                <p className="text-lg font-bold text-gray-900">{log.ai_results.geo_validation.score}/100</p>
                              </div>
                              <div className="bg-purple-50 p-3 rounded-lg">
                                <p className="text-xs text-purple-600 mb-1">Validé le</p>
                                <p className="text-sm text-gray-900">
                                  {log.ai_results.geo_validation.validated_at ? 
                                    new Date(log.ai_results.geo_validation.validated_at).toLocaleDateString('fr-FR') 
                                    : 'N/A'}
                                </p>
                              </div>
                            </div>
                            
                            {log.ai_results.geo_validation.reasons?.length > 0 && (
                              <div>
                                <p className="text-xs text-gray-600 mb-2">Raisons de la décision :</p>
                                <ul className="space-y-1">
                                  {log.ai_results.geo_validation.reasons.map((reason, i) => (
                                    <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                                      <span className="text-purple-500">•</span>
                                      {reason}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        )
                      )}
                      
                      {/* Métadonnées */}
                      <div className="flex items-center gap-6 text-xs text-gray-500 pt-3 border-t">
                        <span>Spot ID: {log.spot_id}</span>
                        {log.processing_time && <span>⏱️ Traitement: {log.processing_time.toFixed(2)}s</span>}
                        {log.status && <span>📊 Statut: {log.status}</span>}
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
